/* RunCard (DESIGN14 §F1.6) — the shared async-run card for the mini app.

   A SELF-DRIVING component: give it the run a POST just returned (via the
   `run` prop) and the three API callbacks, and it polls to completion,
   resumes an in-flight run on mount (/runs/active), renders the Telegram-
   styled stepper with elapsed time + the round-12 last-signal liveness line,
   and offers a quiet Stop (confirmed through the native Telegram dialog).

   It is generic over the run shape so all three surfaces reuse it verbatim —
   the poster tab here and F2's estimate / proposal tabs — since Run,
   EstimateRun and ProposalRun all structurally satisfy RunLike and share the
   same stage vocabulary. Reuses the exact round-12 semantics (stageStep,
   useRunLiveness, isOperatorStop) from the web RunProgress so the two
   surfaces never drift.

   Contract for callers:
   • Keep RunCard mounted inside the tab. Pass the freshly-created run to
     `run`; RunCard adopts any run whose id it has not seen and drives it.
   • On success `onDone(run)` fires — refetch your document/spec there and
     clear your pending-run state. On failure `onError(msg, run)` fires
     (an operator Stop is NOT a failure — it resolves quietly).
   • `onBusyChange(busy)` tracks whether a run is live so the parent can gate
     its own controls (MainButton, inputs). */

import React, { useEffect, useRef, useState } from 'react';
import {
  useRunLiveness,
  isOperatorStop,
  stageStep,
} from '../../architect/RunProgress';
import { mapKnownRunMessage } from '../../architect/utils';
import { haptic, showConfirm } from '../telegram';

export type RunCardStatus = 'running' | 'done' | 'error';

/** The minimal run shape RunCard drives. Run / EstimateRun / ProposalRun all
    satisfy it structurally (extra fields like estimate_id are read by the
    caller after casting in onDone). */
export interface RunLike {
  id: string;
  status: RunCardStatus;
  stage: string;
  model: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface RunCardProps<R extends RunLike> {
  projectId: string;
  /** The run to drive (what your generate/prompt POST returned). Pass null
      when idle; RunCard adopts any run whose id it hasn't tracked yet. */
  run: R | null;
  /** Poll a run by id. */
  poll: (runId: string) => Promise<{ run: R }>;
  /** Cancel a run (idempotent server-side). */
  canCancel?: boolean;
  cancel: (runId: string) => Promise<{ run: R }>;
  /** Resume the project's active/most-recent run on mount. Omit to skip. */
  getActive?: (projectId: string) => Promise<{ run: R | null }>;
  heading: string;
  /** Map a wire model id to its white-label tier ('Ultra'/'Lite'/…). */
  modelLabel?: (m: string | null) => string | null;
  /** Optional italic excerpt (e.g. a prompt instruction). */
  excerpt?: string | null;
  /** Override the five step labels (default: generic document labels). */
  steps?: string[];
  onDone: (run: R) => void;
  onError?: (message: string, run: R) => void;
  onBusyChange?: (busy: boolean) => void;
  /** Called after the initial active-run lookup, including an empty or failed
      lookup. Lets a recovery action wait until it knows no run is live. */
  onResumeChecked?: () => void;
  /** Successful lookup only; recovery callers must not assume a failed check means idle. */
  onResumeResult?: (run: R | null) => void;
}

const POLL_MS = 1500;
const STALE_MS = 60 * 1000;
const LONG_WAIT_MS = 10 * 60 * 1000;

const STEPS_DEFAULT = [
  'Prepare sources',
  'AI writes the document',
  'Validate',
  'Render',
  'Save',
];
const STEP_DESC = [
  'Gathering your files and notes',
  'The AI reads the sources and builds the document',
  'Checking the document against the schema',
  'Turning the validated model into the document',
  'Saving the new version',
];
const STEP_PCT = [8, 38, 62, 82, 94];
const RETRY_LABEL = 'Re-checking & rewriting (attempt 2)';
const RETRY_DESC =
  'Validation flagged something — the AI is fixing it. This is normal.';

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

export function RunCard<R extends RunLike>(props: RunCardProps<R>) {
  const { projectId, run, heading, modelLabel, excerpt } = props;
  const steps = props.steps || STEPS_DEFAULT;

  const [active, setActive] = useState<R | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Latest props for the long-lived poll loop.
  const pRef = useRef(props);
  pRef.current = props;

  const pollTimer = useRef<number | null>(null);
  const pollIdRef = useRef<string | null>(null);
  const lastAdoptedRef = useRef<string | null>(null);
  const busyRef = useRef(false);

  const stopPolling = () => {
    if (pollTimer.current != null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const setBusy = (b: boolean) => {
    if (busyRef.current === b) return;
    busyRef.current = b;
    pRef.current.onBusyChange?.(b);
  };

  const clear = () => {
    stopPolling();
    pollIdRef.current = null;
    setActive(null);
    setBusy(false);
  };

  const beginPolling = (runId: string) => {
    stopPolling();
    pollIdRef.current = runId;
    let inFlight = false;
    let settled = false;
    const tick = async () => {
      if (inFlight || settled) return;
      inFlight = true;
      let r: R;
      try {
        r = (await pRef.current.poll(runId)).run;
      } catch (e: unknown) {
        inFlight = false;
        const status = (e as { status?: number }).status;
        if (status === 404) {
          settled = true;
          clear();
          pRef.current.onError?.('Generation lost — start it again.', {
            id: runId,
          } as R);
        }
        return;
      }
      inFlight = false;
      if (settled || pollIdRef.current !== runId) return;
      setActive(r);
      if (r.status === 'done') {
        settled = true;
        stopPolling();
        haptic.notify('success');
        pRef.current.onDone(r);
        clear();
      } else if (r.status === 'error') {
        settled = true;
        stopPolling();
        if (isOperatorStop(r.error)) {
          haptic.notify('warning');
          clear();
        } else {
          haptic.notify('error');
          pRef.current.onError?.(
            mapKnownRunMessage(r.error) || 'Could not finish — try again.',
            r,
          );
          clear();
        }
      }
    };
    void tick();
    pollTimer.current = window.setInterval(tick, POLL_MS);
  };

  const adopt = (r: R) => {
    lastAdoptedRef.current = r.id;
    setActive(r);
    setBusy(true);
    beginPolling(r.id);
  };

  // Adopt a run handed down via the prop (a fresh POST result).
  useEffect(() => {
    if (run && run.status === 'running' && run.id !== lastAdoptedRef.current) {
      adopt(run);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id]);

  // Resume an in-flight run on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pRef.current.getActive) { pRef.current.onResumeChecked?.(); return; }
      try {
        const { run: r } = await pRef.current.getActive(projectId);
        if (cancelled) return;
        pRef.current.onResumeResult?.(r);
        if (!r) return;
        if (r.status === 'running' && r.id !== lastAdoptedRef.current) adopt(r);
      } catch {
        /* nothing to resume */
      } finally {
        if (!cancelled) pRef.current.onResumeChecked?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Elapsed ticker from the run's wall-clock start (survives resume).
  // created_at is immutable per run id, so these two deps re-arm the
  // ticker exactly once per adopted run.
  const activeId = active ? active.id : null;
  const activeCreatedAt = active ? active.created_at : null;
  useEffect(() => {
    if (!activeId) {
      setElapsedMs(0);
      return undefined;
    }
    const start = (activeCreatedAt ? Date.parse(activeCreatedAt) : NaN) || Date.now();
    const upd = () => setElapsedMs(Math.max(0, Date.now() - start));
    upd();
    const t = window.setInterval(upd, 1000);
    return () => window.clearInterval(t);
  }, [activeId, activeCreatedAt]);

  // Tear down on unmount.
  useEffect(() => () => stopPolling(), []);

  const onStop = async () => {
    const r = active;
    if (!r || pRef.current.canCancel === false) return;
    haptic.impact('medium');
    const ok = await showConfirm('Stop this generation?');
    if (!ok) return;
    try {
      const { run: stopped } = await pRef.current.cancel(r.id);
      if (isOperatorStop(stopped.error) || stopped.status === 'error') {
        haptic.notify('warning');
        clear();
      } else if (stopped.status === 'done') {
        // Raced with a natural finish — hand it to onDone.
        haptic.notify('success');
        pRef.current.onDone(stopped);
        clear();
      } else {
        // Still running (rare) — keep polling.
        setActive(stopped);
      }
    } catch {
      /* leave the run polling; the operator can retry Stop */
    }
  };

  const sinceSignalMs = useRunLiveness(active?.updated_at);

  if (!active) return null;

  const stepIndex = stageStep(active.stage);
  const pct = STEP_PCT[stepIndex] ?? STEP_PCT[0];
  const retry = active.stage === 'llm-retry';
  const onAiStep = active.stage === 'llm' || active.stage === 'llm-retry';
  const stale = sinceSignalMs != null && sinceSignalMs > STALE_MS;
  const longWait = onAiStep && elapsedMs >= LONG_WAIT_MS;
  const chip = modelLabel ? modelLabel(active.model) : null;

  return (
    <div className="tma-run" role="status" aria-live="polite">
      <div
        className="tma-run-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="tma-run-bar-fill" style={{ width: pct + '%' }} />
      </div>
      <div className="tma-run-head">
        <span className="tma-run-title">{heading}</span>
        <span className="tma-run-head-right">
          {chip ? <span className="tma-run-chip">{chip}</span> : null}
          {props.canCancel !== false ? <button type="button" className="tma-run-stop" onClick={onStop}>
            Stop
          </button> : null}
        </span>
      </div>
      {excerpt ? <div className="tma-run-excerpt">«{excerpt}»</div> : null}
      <ol className="tma-run-steps">
        {steps.map((label, i) => {
          const state = i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'future';
          const isCurrent = state === 'current';
          const showRetry = isCurrent && retry;
          const displayLabel = showRetry ? RETRY_LABEL : label;
          const desc = showRetry ? RETRY_DESC : STEP_DESC[i];
          return (
            <li key={i} className={'tma-run-step ' + state + (showRetry ? ' retry' : '')}>
              <span className="tma-run-marker">
                {state === 'done' ? (
                  '✓'
                ) : isCurrent ? (
                  <span className="tma-spin tma-run-spin" />
                ) : (
                  <span className="tma-run-dot" />
                )}
              </span>
              <span className="tma-run-step-body">
                <span className="tma-run-step-label">{displayLabel}</span>
                {isCurrent ? (
                  <span className="tma-run-elapsed">{fmtElapsed(elapsedMs)}</span>
                ) : null}
                {isCurrent ? <span className="tma-run-desc">{desc}</span> : null}
                {isCurrent && sinceSignalMs != null ? (
                  <span
                    className={'tma-run-signal' + (stale ? ' stale' : '')}
                    role={stale ? 'alert' : undefined}
                  >
                    {stale
                      ? 'No signal for a while — if this persists the run will be marked lost automatically'
                      : 'Working — last signal ' +
                        Math.round(sinceSignalMs / 1000) +
                        's ago'}
                  </span>
                ) : null}
                {isCurrent && longWait ? (
                  <span className="tma-run-hint">
                    A large document can take 10–15 minutes.
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      <div className="tma-run-caption">
        You can close this — generation continues on the server.
      </div>
    </div>
  );
}

export default RunCard;
